import React, { memo, useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import { getFeatureFlagsConfiguration, getGlobalConfiguration } from "../../utils/getGlobalConfiguration";
import { useTranslation } from "react-i18next";
import { viewPolicyPath } from "../../router/paths";
import { showMultipleToasts } from "../../utils/apiSetup";
import { featureFlags } from "../../components/FeatureFlag/featureFlagConst";
import { isCustomEvent } from "../guards";
import { featureFlagsChangeAtom } from "../../atoms/atoms";
import { useAtom } from "jotai";
import { useSelector } from "react-redux";
import { State } from "../../configureStore";

interface PoliciesProps {
  id: string;
}

const Policies = memo(function Policies({ id }: PoliciesProps) {
  const [eventListenerRef, setEventListenerRef] = useState<HTMLElement | null>(null);
  const { t } = useTranslation();
  const history = useHistory();
  const isLoading = useSelector((state: State) => state.api.configurations.agentConfigurations.isFetching);
  const [featureFlagValues] = useAtom(featureFlagsChangeAtom);

  const getCancelCodes = () => {
    const cancelCodes = getGlobalConfiguration("cancelCodes");
    return Object.keys(cancelCodes).map((code) => ({ key: code, value: cancelCodes[code] }));
  };

  const cancelCodes = JSON.stringify(getCancelCodes());

  const translations = JSON.stringify({
    policy: t("portal:widgets.policyList", { returnObjects: true, interpolation: { skipOnVariables: true } }),
    cancelCodes: t("portal:cancelCodes", { returnObjects: true, interpolation: { skipOnVariables: true } }),
    policyStatus: t("portal:policyStatus", { returnObjects: true, interpolation: { skipOnVariables: true } }),
  });

  const featureFlag = JSON.stringify({
    policies: {
      searchFilter: getFeatureFlagsConfiguration(featureFlags.customer.customerView.policiesHeader.searchFilter),
    },
    policy: {
      view: getFeatureFlagsConfiguration(featureFlags.customer.customerView.policyActions.view),
      cancel: getFeatureFlagsConfiguration(featureFlags.customer.customerView.policyActions.cancel),
      addNoteToPolicy: getFeatureFlagsConfiguration(featureFlags.customer.customerView.policyActions.addNoteToPolicy),
      addGeneralNoteToPolicy: getFeatureFlagsConfiguration(
        featureFlags.customer.customerView.policyActions.addGeneralNoteToPolicy,
      ),
    },
  });

  const pageSize = getGlobalConfiguration("portfolioWidgets.policiesPageSize");

  useEffect(() => {
    const viewPolicy = (event: Event) => {
      const { uuid } = (event as CustomEvent<{ uuid: string }>).detail;
      history.push(viewPolicyPath(id, uuid));
    };
    const handleReturnErrors = (event: Event) => {
      if (!isCustomEvent<{ errors?: string[] }>(event)) return;

      const { errors } = event.detail;

      if (errors) {
        showMultipleToasts(errors);
      }
    };

    const handleReturnMessages = (event: Event) => {
      if (!isCustomEvent<{ messages?: string[] }>(event)) return;

      const { messages } = event.detail;

      if (messages) {
        showMultipleToasts(messages);
      }
    };

    const handleOnSuccessAction = (event: Event) => {
      if (!isCustomEvent<{ messages?: string[]; action?: string }>(event)) return;

      const { messages, action } = event.detail;
      if (action === "fetch-policies") return;
      if (messages) showMultipleToasts(messages);
    };

    if (eventListenerRef) {
      eventListenerRef.addEventListener("policy-view", viewPolicy);
      eventListenerRef.addEventListener("success-widget-action", handleOnSuccessAction);
      eventListenerRef.addEventListener("widget-message", handleReturnMessages);
      eventListenerRef.addEventListener("widget-error", handleReturnErrors);
    }

    return () => {
      if (eventListenerRef) {
        eventListenerRef.removeEventListener("policy-view", viewPolicy);
        eventListenerRef.removeEventListener("success-widget-action", handleOnSuccessAction);
        eventListenerRef.removeEventListener("widget-message", handleReturnMessages);
        eventListenerRef.removeEventListener("widget-error", handleReturnErrors);
      }
    };
  }, [eventListenerRef, history, id]);

  //  Widget must not render until feature flags are resolved.
  //  featureFlagValues is null on first render (before API responds),
  //  and isLoading covers the gap while agentConfigurations API is in flight.
  //  Without this guard, the widget renders with fallback flag values and flickers.
  if (isLoading || !featureFlagValues) {
    return null;
  }

  return (
    <ds-portfolio-policy-widget
      pagesize={pageSize}
      ref={setEventListenerRef}
      variant="policy"
      partyid={id}
      cancelcodes={cancelCodes}
      translations={translations}
      featureflags={featureFlag}
    />
  );
});

export default Policies;
